import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import { getWriterBySlug } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

const BORMULA_LOGO = "https://customer-assets.emergentagent.com/job_a6e857fc-9f8a-42a4-a234-830c8a3cc714/artifacts/98ozj9ol_Bormula%20Live%20Logo%201080x1350%20%281%29.png";

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const categoryColors = {
  news: 'bg-[#CCFF00] text-black',
  memes: 'bg-purple-500 text-white',
  'race-updates': 'bg-red-500 text-white',
  reels: 'bg-blue-500 text-white',
};

export default function WriterPage() {
  const { slug } = useParams();
  const [writer, setWriter] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWriter = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getWriterBySlug(slug);
      setWriter(data.writer);
      setArticles(data.articles);
    } catch (err) {
      setError('Writer not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchWriter();
    window.scrollTo(0, 0);
  }, [fetchWriter]);

  useSEO(writer ? {
    title: writer.name,
    description: writer.bio || 'Articles by ' + writer.name + ' on BORMULA.',
    image: writer.image_url,
    url: 'https://bormula.com/writer/' + writer.slug,
  } : {});

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !writer) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-6">
        <h1 className="font-display text-4xl font-bold text-white uppercase tracking-tighter">Writer Not Found</h1>
        <Link to="/" className="flex items-center gap-2 text-[#CCFF00] hover:underline" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="writer-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={BORMULA_LOGO} alt="Bormula" className="h-10 w-10 object-contain" />
              <span className="font-display text-2xl font-bold text-white tracking-tighter">BORMULA</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white/80 hover:text-[#CCFF00] transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </nav>

      {/* Writer Profile Header */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 border-b border-white/5">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center sm:items-start gap-8">
          {/* Avatar */}
          {writer.image_url ? (
            <img
              src={writer.image_url}
              alt={writer.name}
              className="w-28 h-28 rounded-full object-cover border-2 border-[#CCFF00]/30 flex-shrink-0"
              data-testid="writer-avatar"
            />
          ) : (
            <div
              className="w-28 h-28 rounded-full bg-[#CCFF00]/10 border-2 border-[#CCFF00]/30 flex items-center justify-center flex-shrink-0"
              data-testid="writer-avatar-placeholder"
            >
              <span className="font-display text-3xl font-bold text-[#CCFF00]">{getInitials(writer.name)}</span>
            </div>
          )}

          <div className="text-center sm:text-left">
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-white uppercase tracking-tighter mb-4" data-testid="writer-name">
              {writer.name}
            </h1>
            {writer.bio && (
              <p className="text-white/60 text-lg leading-relaxed max-w-2xl" data-testid="writer-bio">
                {writer.bio}
              </p>
            )}
            <div className="mt-4 text-xs font-bold uppercase tracking-widest text-white/30">
              {articles.length} {articles.length === 1 ? 'Article' : 'Articles'} Published
            </div>
          </div>
        </div>
      </section>

      {/* Articles by Writer */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-white uppercase tracking-tighter mb-10" data-testid="writer-articles-heading">
            Articles by {writer.name}
          </h2>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group"
                  data-testid={`writer-article-${article.slug}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-4">
                    <img
                      src={article.image_url || 'https://images.unsplash.com/photo-1749952648908-d20b76c078dc'}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest ${categoryColors[article.category] || categoryColors.news}`}>
                        {article.category}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                  </div>
                  <div className="flex items-center gap-3 text-white/40 text-[10px] font-black uppercase tracking-widest mb-2">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(article.created_at)}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white uppercase leading-tight group-hover:text-[#CCFF00] transition-colors mb-2">
                    {article.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-white/40 text-lg">No articles published yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 justify-center">
            <img src={BORMULA_LOGO} alt="Bormula" className="h-8 w-8 object-contain" />
            <span className="font-display text-2xl font-bold text-[#CCFF00]">BORMULA</span>
          </Link>
          <p className="text-xs text-white/20 mt-2 font-bold uppercase tracking-widest">&copy; 2024 Bormula Media Group</p>
        </div>
      </footer>
    </div>
  );
}
